<!--
  ConsentBulkCard.vue
  보안 설정 「고객 지원을 위한 접근 허용 — 기존 회사 일괄 켜기」(사용자 결정 10-29, 계약 SUPPORT 0-1절).

  - 기록이 없는 고객사(병원·제조사·기공소·STUDIO)만 켬. 고객이 직접 고른 회사(꺼짐 포함)는 건너뜀.
  - 고객사에는 메일·이용 내역이 남지 않고, 운영자 이용 내역에만 1건(회사 없음).
  - ops.support.manage(지정자)만 보임. 실행은 M! — axios 인터셉터가 본인 확인 창을 띄움.
-->
<template>
  <div v-if="canManage" class="card content-wrap-card p-3 consent-bulk">
    <div class="head">
      <h3 class="fw-bold title">고객 지원을 위한 접근 허용 — 기존 회사 일괄 켜기</h3>
      <span class="pageDesc">
        기록이 없는 고객사를 모두 켭니다. 고객이 직접 고른 회사(끈 회사 포함)는 건너뜁니다. 고객사에는 메일과 이용 내역이 남지 않습니다.
      </span>
    </div>

    <div v-if="loadError" class="noResult">
      <div>{{ loadError }}</div>
      <a-button size="small" class="mt-2" @click="load">다시 불러오기</a-button>
    </div>
    <a-spin v-else :spinning="loading">
      <div class="counts">{{ bulkCountsText(counts) || "-" }}</div>
      <div class="actions">
        <a-button type="primary" class="yellow-button" :disabled="!counts || !counts.noRecord" @click="open = true">
          기록 없는 {{ counts ? counts.noRecord : 0 }}곳 켜기
        </a-button>
        <a-button size="small" type="link" @click="load">다시 불러오기</a-button>
      </div>
      <p v-if="result" class="result" role="status">{{ result }}</p>
    </a-spin>

    <a-modal :visible="open" :confirm-loading="saving" :footer="null" @cancel="close">
      <template #title>
        <div class="modal-title">기존 회사 일괄 켜기</div>
      </template>
      <div class="modal-content">
        <p class="sub-text">{{ bulkCountsText(counts) }}</p>
        <p class="sub-text">켜면 서티라이프와 담당 리셀러가 그 회사에 지원 접속할 수 있습니다. 고객사 관리자는 언제든 끌 수 있습니다.</p>
        <label class="field-label">사유(필수)</label>
        <a-textarea v-model:value="reason" :rows="2" :maxlength="BULK_REASON_MAX" placeholder="예: 운영 배포 뒤 기존 고객사 켜기" />
        <div class="modal-footer">
          <a-button @click="close">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="saving" :disabled="!!problem" @click="submit">켜기</a-button>
        </div>
        <p v-if="problem" class="field-hint text-end">{{ problem }}</p>
        <p class="field-hint text-end">실행 전에 운영자 본인 확인을 합니다.</p>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { errorMessage, isStepUpCancelled } from "@/utils/mfa";
import { canNow } from "@/utils/perm";
import { BULK_REASON_MAX, bulkBody, bulkCountsText, bulkDoneText, bulkError } from "@/utils/supportAccess";
import { standardErrorNotice } from "@/utils/standardErrors";
import { ConsentBulkApi } from "@/api/standards";
import { notifyFailure } from "./GeoAllowIp/api";

export default {
  name: "ConsentBulkCard",
  data() {
    return {
      BULK_REASON_MAX,
      bulkCountsText,
      counts: null,
      loading: false,
      loadError: "",
      open: false,
      reason: "",
      saving: false,
      result: "",
    };
  },
  computed: {
    // introspect 를 받기 전엔 숨김, perms 칸이 없으면(옛 inapi) 숨기지 않음 — 서버가 판정
    canManage() {
      return canNow(this.$store.state.introspect, "ops.support.manage");
    },
    problem() {
      return bulkError(this.reason);
    },
  },
  // 새로 고침·주소 직접 열기면 introspect 가 늦게 옴 → 권한이 정해진 뒤 한 번 읽음
  watch: {
    canManage: {
      immediate: true,
      handler(v) {
        if (v && !this.counts && !this.loading) this.load();
      },
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        this.counts = await ConsentBulkApi.preview();
      } catch (error) {
        console.error(error);
        this.loadError = isStepUpCancelled(error)
          ? "본인 확인을 취소해 대상 수를 불러오지 못했습니다."
          : errorMessage(error, "대상 수를 불러오지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.loading = false;
      }
    },
    close() {
      if (this.saving) return;
      this.open = false;
      this.reason = "";
    },
    async submit() {
      if (this.problem) return;
      this.saving = true;
      try {
        const r = await ConsentBulkApi.run(bulkBody(this.reason));
        this.result = bulkDoneText(r);
        this.counts = r;
        this.saving = false;
        this.close();
      } catch (error) {
        // 인터셉터가 이미 알린 새 계약 코드(AUDIT_UNAVAILABLE·PERMISSION_DENIED 등)는 다시 알리지 않는다
        if (standardErrorNotice(error) || isStepUpCancelled(error)) console.error(error);
        else notifyFailure(error, "켜지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.head {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.title {
  margin: 0;
  font-size: 1.4rem;
  color: #000;
}
.pageDesc {
  font-size: 0.9rem;
  color: #67748e;
}
.counts {
  font-size: 14px;
  color: #2f3337;
  margin-bottom: 10px;
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.result {
  margin: 10px 0 0;
  font-size: 13px;
  color: #1b5e20;
}
.sub-text {
  font-size: 13px;
  color: #5c6168;
}
.field-label {
  display: block;
  margin: 14px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #2f3337;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #5c6168;
}
/* 창 본문 여백(레포 규칙 src/modal.mdc — 전역 .modal-content 테두리·.ant-modal-content padding 0 을 덮음, FIX-1101 12) */
.modal-content {
  padding: 20px;
  border: none;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.consent-bulk :deep(.yellow-button),
.modal-footer :deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;
}
</style>
